import { ApiResponse } from 'apisauce';

import { usersApiService } from '@/Modules/UserManagement/shared/services/users-api.service';
import api from '@/shared/config/http.config';
import { ErrorInfo } from '@/shared/interfaces/error-info.interface';

import { ticketMapper } from '../mappers/ticket.mapper';
import {
  Ticket,
  Comment,
  HelpdeskNotification,
  AnalyticsSummary,
  VolumeDataPoint,
  CategoryDataPoint,
  ResolutionTimeDataPoint,
  TopResolver,
  PaginatedTickets,
  ListTicketsPayload,
  CreateTicketPayload,
  UpdateTicketPayload,
  TicketAttachment
} from '../types';

// Backend (NestJS) returns data directly — no ResponseModel<T> wrapper.
// Apisauce puts the HTTP body in response.data, so we read response.data directly.
function throwIfNotOk<T>(
  response: ApiResponse<T, ErrorInfo>,
  errorMessage: string
): asserts response is ApiResponse<T, ErrorInfo> & { ok: true } {
  if (!response.ok) {
    const errData = response.data as Record<string, unknown> | null | undefined;
    throw new Error(
      errData?.errorCode ? String(errData.errorCode) : response.problem || errorMessage
    );
  }
}

export const helpdeskApiService = {
  async listTickets(payload: ListTicketsPayload): Promise<PaginatedTickets> {
    const response = await api.post<PaginatedTickets, ErrorInfo>(
      '/helpdesk/tickets/search',
      payload
    );
    throwIfNotOk(response, 'Failed to fetch tickets');
    const raw = response.data;
    if (!raw) throw new Error('No ticket data in response');
    return {
      data: raw.data.map(ticketMapper.mapTicket),
      total: raw.total,
      page: raw.page,
      limit: raw.limit
    };
  },

  async getTicketById(id: string): Promise<Ticket> {
    const response = await api.get<unknown, ErrorInfo>(`/helpdesk/tickets/${id}`);
    throwIfNotOk(response, 'Failed to fetch ticket');
    const raw = response.data;
    if (!raw) throw new Error('No ticket data in response');
    return ticketMapper.mapTicket(raw);
  },

  async createTicket(payload: CreateTicketPayload): Promise<Ticket> {
    const response = await api.post<unknown, ErrorInfo>('/helpdesk/tickets', payload);
    throwIfNotOk(response, 'Failed to create ticket');
    const raw = response.data;
    if (!raw) throw new Error('No ticket data in response');
    return ticketMapper.mapTicket(raw);
  },

  async updateTicket(id: string, payload: UpdateTicketPayload): Promise<Ticket> {
    const response = await api.patch<unknown, ErrorInfo>(`/helpdesk/tickets/${id}`, payload);
    throwIfNotOk(response, 'Failed to update ticket');
    const raw = response.data;
    if (!raw) throw new Error('No ticket data in response');
    return ticketMapper.mapTicket(raw);
  },

  async getComments(ticketId: string): Promise<Comment[]> {
    const response = await api.get<unknown[], ErrorInfo>(`/helpdesk/tickets/${ticketId}/comments`);
    throwIfNotOk(response, 'Failed to fetch comments');
    return (response.data ?? []).map(ticketMapper.mapComment);
  },

  async createComment(ticketId: string, body: string): Promise<Comment> {
    const response = await api.post<unknown, ErrorInfo>(`/helpdesk/tickets/${ticketId}/comments`, {
      body
    });
    throwIfNotOk(response, 'Failed to create comment');
    const raw = response.data;
    if (!raw) throw new Error('No comment data in response');
    return ticketMapper.mapComment(raw);
  },

  async getNotifications(): Promise<HelpdeskNotification[]> {
    const response = await api.get<{ data: unknown[]; total: number }, ErrorInfo>(
      '/helpdesk/notifications'
    );
    throwIfNotOk(response, 'Failed to fetch notifications');
    const items = response.data?.data ?? [];
    return items.map(ticketMapper.mapNotification);
  },

  async markNotificationRead(id: string): Promise<void> {
    const response = await api.patch<unknown, ErrorInfo>(`/helpdesk/notifications/${id}/read`, {});
    throwIfNotOk(response, 'Failed to mark notification as read');
  },

  async markAllNotificationsRead(): Promise<void> {
    const response = await api.patch<unknown, ErrorInfo>('/helpdesk/notifications/read-all', {});
    throwIfNotOk(response, 'Failed to mark all notifications as read');
  },

  async getAttachmentSignedUrl(key: string): Promise<string> {
    const response = await api.post<{ signedUrl: string }, ErrorInfo>(
      '/helpdesk/attachments/getSignedUrl',
      {
        key
      }
    );
    throwIfNotOk(response, 'Failed to get attachment URL');
    return response.data!.signedUrl;
  },

  async presignUrl(
    filename: string,
    mimeType: string
  ): Promise<{ uploadUrl: string; key: string; publicUrl: string }> {
    // Backend DTO field is `fileName` (camelCase) with forbidNonWhitelisted: true.
    // Backend response returns `presignedUrl` and `fileUrl` — mapped to internal names below.
    const response = await api.post<
      { presignedUrl: string; key: string; fileUrl: string },
      ErrorInfo
    >('/helpdesk/attachments/presign', { fileName: filename, mimeType });
    throwIfNotOk(response, 'Failed to get presigned URL');
    const data = response.data;
    if (!data) throw new Error('No presign data in response');
    return {
      uploadUrl: data.presignedUrl,
      key: data.key,
      publicUrl: data.fileUrl
    };
  },

  async getAnalyticsSummary(): Promise<AnalyticsSummary> {
    const response = await api.get<AnalyticsSummary, ErrorInfo>('/helpdesk/analytics/summary');
    throwIfNotOk(response, 'Failed to fetch analytics summary');
    const data = response.data;
    if (!data) throw new Error('No analytics summary data');
    return data;
  },

  async getAnalyticsVolume(days: number): Promise<VolumeDataPoint[]> {
    const response = await api.get<VolumeDataPoint[], ErrorInfo>(
      `/helpdesk/analytics/volume?days=${days}`
    );
    throwIfNotOk(response, 'Failed to fetch analytics volume');
    return response.data ?? [];
  },

  async getAnalyticsByCategory(): Promise<CategoryDataPoint[]> {
    const response = await api.get<CategoryDataPoint[], ErrorInfo>(
      '/helpdesk/analytics/by-category'
    );
    throwIfNotOk(response, 'Failed to fetch analytics by category');
    return response.data ?? [];
  },

  async getAnalyticsResolutionTime(): Promise<ResolutionTimeDataPoint[]> {
    const response = await api.get<ResolutionTimeDataPoint[], ErrorInfo>(
      '/helpdesk/analytics/resolution-time'
    );
    throwIfNotOk(response, 'Failed to fetch resolution time analytics');
    return response.data ?? [];
  },

  async getAnalyticsTopResolvers(): Promise<TopResolver[]> {
    const response = await api.get<{ userId: string; name: string; count: number }[], ErrorInfo>(
      '/helpdesk/analytics/top-resolvers'
    );
    throwIfNotOk(response, 'Failed to fetch top resolvers');
    return (response.data ?? []).map((item) => ({
      userId: item.userId,
      name: item.name,
      resolvedCount: item.count
    }));
  },

  async summarizeTicket(ticketId: string): Promise<{ summary: string }> {
    const response = await api.post<{ summary: string }, ErrorInfo>(
      `/helpdesk/tickets/${ticketId}/summarize`,
      {}
    );
    throwIfNotOk(response, 'Failed to summarize ticket');
    if (!response.data) throw new Error('No summary in response');
    return response.data;
  },

  async polishComment(ticketId: string, draftBody: string): Promise<{ body: string }> {
    const response = await api.post<{ body: string }, ErrorInfo>(
      `/helpdesk/tickets/${ticketId}/comments/polish`,
      { draftBody }
    );
    throwIfNotOk(response, 'Failed to polish comment');
    if (!response.data) throw new Error('No polished body in response');
    return response.data;
  },

  async polishDescription(draftBody: string): Promise<{ body: string }> {
    const response = await api.post<{ body: string }, ErrorInfo>(
      '/helpdesk/tickets/polish-description',
      {
        draftBody
      }
    );
    throwIfNotOk(response, 'Failed to polish description');
    if (!response.data) throw new Error('No polished body in response');
    return response.data;
  },

  async searchHelpdeskUsers(
    name: string,
    role?: string[]
  ): Promise<Array<{ userId: string; name: string }>> {
    const result = await usersApiService.getAllUsers({
      skip: 0,
      limit: 20,
      query: { name, ...(role?.length ? { role } : {}) }
    });
    return result.data
      .filter((u: { id?: string; name?: string }) => !!u.id)
      .map((u: { id?: string; name?: string }) => ({
        userId: u.id as string,
        name: u.name ?? ''
      }));
  }
};

export type { TicketAttachment };
